import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { NoPaddingContainer, StyledAlert } from '../../../../styles/common';
import { useFetchData } from '../../../../utils/hooks';
import { apiPath } from '../../../../constants';

import UserInfoRows from './UserInfoRows';
import DevicesRows from './DevicesRows';
import EmergencyInfoRows from './EmergencyInfoRows';

import { FullHeightCol } from './styles';

const POLLING_INTERVAL = 5000;

const UserDetails = () => {
    const { username } = useParams();
    const { t } = useTranslation();
    const pollingIntervalRef = useRef(null);
    const [userLoading, userData, error, refetchUserData] = useFetchData({
        endpoint: `/user/${username}`,
    });
    const [user, setUser] = useState(null);

    useEffect(() => {
        if (!userLoading && userData) {
            setUser(userData);
        }
    }, [userLoading, userData]);

    useEffect(() => {
        if (!userLoading && user) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const response = await fetch(
                        `${apiPath}/user/${username}`,
                        {
                            credentials: 'same-origin',
                            headers: { 'Content-Type': 'application/json' },
                        },
                    );

                    if (response.ok) {
                        const freshData = await response.json();
                        setUser((prev) => ({
                            ...prev,
                            idVerificationStatus:
                                freshData.idVerificationStatus,
                            lastVerificationDate:
                                freshData.lastVerificationDate,
                            lastUpdatedVerificationDate:
                                freshData.lastUpdatedVerificationDate,
                            identityVerificationStepOutcome:
                                freshData.identityVerificationStepOutcome,
                            passwordGenerationStepOutcome:
                                freshData.passwordGenerationStepOutcome,
                            tapGenerationConfigured:
                                freshData.tapGenerationConfigured,
                            adPasswordGenerationConfigured:
                                freshData.adPasswordGenerationConfigured,
                        }));
                    }
                } catch (error) {
                    console.error('Polling error:', error);
                }
            }, POLLING_INTERVAL);
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [userLoading, user, username]);

    // Check for privilege denied error (403 Forbidden)
    // Error can be either a string "Forbidden" or an object with message property
    const isForbidden = error && (
        error === 'Forbidden' ||
        error.message === 'Forbidden' ||
        (typeof error === 'object' && error.toString().includes('Forbidden'))
    );

    if (isForbidden) {
        return (
            <NoPaddingContainer>
                <Row>
                    <Col>
                        <StyledAlert color="danger" fade={false}>
                            {t('errorMsgs$privilegeDenied')}
                        </StyledAlert>
                    </Col>
                </Row>
            </NoPaddingContainer>
        );
    }

    return (
        <NoPaddingContainer>
            <Row>
                <FullHeightCol sm="12" md="12" $reduceSpace="none">
                    <UserInfoRows
                        userLoading={userLoading}
                        user={user}
                        onRefreshData={refetchUserData}
                    />
                </FullHeightCol>
            </Row>
            <Row>
                <Col>
                    <DevicesRows userData={user} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <EmergencyInfoRows userData={user} />
                </Col>
            </Row>
        </NoPaddingContainer>
    );
};

export default UserDetails;
